import { useEffect, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@reflex/ui/components/ui/sheet';
import { Button } from '@reflex/ui/components/ui/button';
import { cn } from '@reflex/ui/lib/utils';

interface MobileToolsSheetProps {
  /**
   * Rail overflow (Details, Display, pinned actions, sandbox). Pass a render
   * function to dismiss this sheet when an action opens another overlay
   * (Report bug, Details) so the modal does not trap pointer events.
   */
  tools: ReactNode | ((dismiss: () => void) => ReactNode);
  className?: string;
}

/**
 * Icon-only overflow trigger that opens a bottom sheet of rail actions so they
 * are not lost when the vertical rail is hidden below `md`.
 */
export function MobileToolsSheet({ tools, className }: MobileToolsSheetProps) {
  const [open, setOpen] = useState(false);
  const toolsNode = typeof tools === 'function' ? tools(() => setOpen(false)) : tools;

  useEffect(() => {
    const close = () => setOpen(false);
    window.addEventListener('plugin:command', close);
    return () => window.removeEventListener('plugin:command', close);
  }, []);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'h-10 min-h-10 gap-1 rounded-none border-0 px-2.5 py-0 shadow-none',
            'text-foreground hover:bg-muted/40 hover:text-foreground',
            'data-[state=open]:bg-muted/40',
            className,
          )}
          aria-label="Actions"
          data-testid="mobile-tools-sheet-trigger"
        >
          <ChevronDown className="size-3.5 shrink-0" aria-hidden="true" />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        showCloseButton
        className="gap-0 rounded-t-xl p-0 sm:max-w-none"
        data-testid="mobile-tools-sheet"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <SheetHeader className="border-b border-border/40 px-4 py-3 text-left">
          <SheetTitle className="text-sm font-semibold">Actions</SheetTitle>
        </SheetHeader>

        {toolsNode ? (
          <div className="px-3 pb-4 pt-3" data-testid="mobile-tools-sheet-grid">
            {toolsNode}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
